import { Dtos, SuretyAccountId } from "@inrev/common";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createVersionConflictHandler } from "../../utils/error";
import { useRequest } from "../../utils/request";
import { accountMetadataQueryKey, accountQueryKey } from "./account.keys";

export const useUpdateAccountHistory = (
	accountId: SuretyAccountId,
	options?: { onConflict?: () => void },
) => {
	const queryClient = useQueryClient();
	const { user } = useAuthenticatedUser();
	const isAdmin = user.type === "admin";
	const { patch } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const staleKeys = [accountQueryKey(accountId), accountMetadataQueryKey(accountId)];

	const mutation = useMutation({
		mutationFn: async (dto: Dtos.SuretyAccount.Update.History.Request) => {
			const url = isAdmin
				? `/v2/admin/surety/accounts/${accountId}/metadata`
				: `/v2/surety/accounts/${accountId}/metadata`;
			return await patch<void>(url, dto);
		},
		onSuccess: () => {
			for (const queryKey of staleKeys) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
		onError: createVersionConflictHandler(staleKeys, queryClient, triggerErrorModal, {
			refetchType: "active",
			onConflict: options?.onConflict,
		}),
	});

	return {
		updateAccountHistory: mutation.mutate,
		updateAccountHistoryIsLoading: mutation.isPending,
		updateAccountHistoryError: mutation.error,
	};
};
